import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@heyroot/ui/primitives/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@heyroot/ui/primitives/Table';
import { fetchAnalytics, queryKeys } from '../lib/api';

function BarCell({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 rounded-full bg-surface-sunken overflow-hidden">
        <div className="h-2 rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-caption text-secondary tabular-nums">{value}</span>
    </div>
  );
}

function CsatDot({ score }: { score: number | null }) {
  if (score === null) return <span className="text-caption text-muted">—</span>;
  const color = score >= 4 ? 'bg-success' : score >= 3 ? 'bg-warning' : 'bg-danger';
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block w-2 h-2 rounded-full ${color}`} />
      <span className="text-caption text-secondary tabular-nums">{score.toFixed(1)}</span>
    </div>
  );
}

function defaultFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 29);
  return d.toISOString().slice(0, 10);
}

function defaultTo() {
  return new Date().toISOString().slice(0, 10);
}

export function AssistantAnalyticsPage() {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [tenantId, setTenantId] = useState('');
  const fieldId = useId();

  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.analytics({ from, to, tenantId: tenantId || null }),
    queryFn: () => fetchAnalytics({ from, to, tenantId: tenantId || null }),
    enabled: !!from && !!to,
  });

  const rows = data?.rows ?? [];
  const maxConversations = Math.max(1, ...rows.map((r) => r.conversationsCount));
  const maxEscalations = Math.max(1, ...rows.map((r) => r.escalationsCount));

  // Aggregate totals for summary strip
  const totalConversations = rows.reduce((s, r) => s + r.conversationsCount, 0);
  const totalEscalations = rows.reduce((s, r) => s + r.escalationsCount, 0);
  const csatScores = rows.flatMap((r) => (r.csatAvg !== null ? [r.csatAvg] : []));
  const avgCsat =
    csatScores.length > 0 ? csatScores.reduce((a, b) => a + b, 0) / csatScores.length : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-heading-2 text-primary">Assistant Analytics</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label htmlFor={`${fieldId}-from`} className="text-caption text-muted">
            From
          </label>
          <Input
            id={`${fieldId}-from`}
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-40"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${fieldId}-to`} className="text-caption text-muted">
            To
          </label>
          <Input
            id={`${fieldId}-to`}
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-40"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${fieldId}-tenant`} className="text-caption text-muted">
            Tenant ID (optional)
          </label>
          <Input
            id={`${fieldId}-tenant`}
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="Filter by tenant…"
            className="w-64"
          />
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-4">
        {(
          [
            { label: 'Conversations', value: totalConversations.toLocaleString() },
            { label: 'Escalations', value: totalEscalations.toLocaleString() },
            { label: 'Avg CSAT', value: avgCsat !== null ? avgCsat.toFixed(2) : '—' },
          ] as const
        ).map((s) => (
          <div key={s.label} className="rounded-control border border-border bg-surface p-4">
            <p className="text-caption text-muted">{s.label}</p>
            <p className="text-heading-2 text-primary tabular-nums">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Data table */}
      {isPending ? (
        <div className="text-body-sm text-muted animate-pulse">Loading…</div>
      ) : isError ? (
        <div role="alert" className="text-body-sm text-danger-text">
          Failed to load analytics.
        </div>
      ) : rows.length === 0 ? (
        <div className="text-body-sm text-muted">No data for the selected range.</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Date</TableHeaderCell>
                <TableHeaderCell>Tenant</TableHeaderCell>
                <TableHeaderCell>Conversations</TableHeaderCell>
                <TableHeaderCell>Escalations</TableHeaderCell>
                <TableHeaderCell>Avg resolution (min)</TableHeaderCell>
                <TableHeaderCell>CSAT</TableHeaderCell>
                <TableHeaderCell>Confidence tiers</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.date}-${row.tenantId}`}>
                  <TableCell className="text-caption text-secondary">{row.date}</TableCell>
                  <TableCell className="text-caption text-secondary font-mono">
                    {row.tenantId.slice(0, 8)}…
                  </TableCell>
                  <TableCell>
                    <BarCell value={row.conversationsCount} max={maxConversations} />
                  </TableCell>
                  <TableCell>
                    <BarCell value={row.escalationsCount} max={maxEscalations} />
                  </TableCell>
                  <TableCell className="text-caption text-secondary tabular-nums">
                    {row.avgResolutionMinutes !== null ? row.avgResolutionMinutes.toFixed(1) : '—'}
                  </TableCell>
                  <TableCell>
                    <CsatDot score={row.csatAvg} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2 text-caption text-secondary">
                      <span className="text-success-text">
                        H:{row.confidenceTierDistribution.high}
                      </span>
                      <span className="text-warning-text">
                        M:{row.confidenceTierDistribution.medium}
                      </span>
                      <span className="text-danger-text">
                        L:{row.confidenceTierDistribution.low}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
